<template>
  <div>
    <PageDynamicBreadcrumb />
    <PageHeader title="Insurance Agents Pending Signups" />

    <DataTable
      table-key="offersOverview"
      row-key="id"
      title=""
      :columns="columns"
      :rows="pendingSignups"
      :loading="loading"
      :pagination="pagination"
      :downloadable="true"
      :refreshable="false"
      :customizable="false"
      :filterable="true"
      :searchable="true"
      :disable-state="true"
    >
      <template v-slot:status="row">
        <q-badge
          :color="{
            0: 'subtle',
            1: 'subtle',
            2: 'warning',
            3: 'positive'
          }[row.status_code]"
          text-color="white"
          class="text-weight-medium q-py-xs q-px-sm flex items-center justify-between"
        >
          {{ row.status }}
        </q-badge>
      </template>
      <template v-slot:actions="row">
        <ActionsDropdownButton
          :actions="generatePendingSignupActions(row)"
        />
      </template>
    </DataTable>
  </div>
</template>

<script>
import ConfirmationModalDelete from "@/components/UI/ConfirmationModalDelete";
import { Dialog } from "quasar";
import PageHeader from "@/components/UI/PageHeader";
import PageDynamicBreadcrumb from "@/components/UI/PageDynamicBreadcrumb";
import DataTable from "@/components/DataTable";
import ActionsDropdownButton from "@/components/UI/ActionsDropdownButton";
import axios from "axios";
import moment from "moment";
import ConfirmationModal from "@/components/UI/ConfirmationModal.vue";

export default {
  name: "InsuranceAgentsPendingSignupsOverview",
  components: {
    ActionsDropdownButton,
    DataTable,
    PageDynamicBreadcrumb,
    PageHeader
  },
  data() {
    return {
      loading: true,
      filter: "",
      pendingSignups: [],
      pagination: {
        sortBy: "id",
        descending: true,
        page: 1,
        rowsPerPage: 25
      },
      columns: [
        {
          name: "status",
          label: "Status",
          field: "status",
          hideField: true
        },
        {
          name: "id",
          label: "Signup ID",
          field: "id",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension"
        },
        {
          name: "time_created",
          label: "Signup Time",
          field: row =>
              row.time_created ? moment.utc(row.time_created).local() : "",
          format: val => (!val ? "-" : moment(val).format("lll")),
          sortable: true,
          sort: (a, b) => this.sortDate(a, b),
          align: "left",
          type: "metric",
          metricType: "date"
        },
        {
          name: "business_name",
          label: "Agency Name",
          field: "business_name",
          sortable: true,
          type: "dimension"
        },
        {
          name: "agency_type",
          label: "Agency Type",
          field: "agency_type",
          sortable: true,
          type: "dimension"
        },
        {
          name: "claimed_business_id",
          label: "Claimed Business ID",
          field: row => row.claimed_business_id.length > 0 ? row.claimed_business_id : "-",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension"
        },
        {
          name: "business_phone",
          label: "Business Phone",
          field: "business_phone",
          format: val => this.formatPhoneNumber(val),
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension"
        },
        {
          name: "business_website_url",
          label: "Website Url",
          field: row => row.business_website_url.length > 0 ? row.business_website_url : "-",
          sortable: true,
          type: "dimension"
        },
        {
          name: "address",
          label: "Address",
          field: "address",
          sortable: true,
          type: "dimension"
        },
        {
          name: "city",
          label: "City",
          field: "city",
          sortable: true,
          type: "dimension"
        },
        {
          name: "state",
          label: "State",
          field: "state",
          sortable: true,
          type: "dimension"
        },
        {
          name: "postal_code",
          label: "Postal Code",
          field: "postal_code",
          sortable: true,
          type: "dimension"
        },
        {
          name: "contact_first_name",
          label: "Contact First Name",
          field: "contact_first_name",
          sortable: true,
          type: "dimension"
        },
        {
          name: "contact_last_name",
          label: "Contact Last Name",
          field: "contact_last_name",
          sortable: true,
          type: "dimension"
        },
        {
          name: "contact_phone",
          label: "Contact Phone",
          field: "contact_phone",
          format: val => this.formatPhoneNumber(val),
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension"
        },
        {
          name: "contact_email",
          label: "Contact Email",
          field: "contact_email",
          sortable: true,
          type: "dimension"
        },
        {
          name: "actions",
          label: "",
          field: "",
          filterable: false
        }
      ]
    };
  },
  computed: {},
  mounted() {
    this.getPendingSignups();
  },
  methods: {
    getPendingSignups() {
      this.loading = true;

      axios("http://localhost:8082/professionals/pending-business-signups")
        .then(resp => {
          this.pendingSignups = resp.data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generatePendingSignupActions(pendingSignup) {
      return [
        {
          action: () => this.$router.push({
            name: "InsuranceAgentsBusinessCreator",
            params: { pendingSignupId: pendingSignup.id }
          }),
          label: "Create Business",
          disabled: pendingSignup.business_created == "1" || pendingSignup.completed == "1",
          disabledTooltip: pendingSignup.completed == "1" ? "Signup completed." : "Business record(s) already created."
        },
        {
          action: () => window.open("https://agents-dashboard.jumpfeed.com/sign-in?bid=" + pendingSignup.claimed_business_id, "_blank"),
          label: "Configure Account",
          disabled: pendingSignup.business_created == "0" || pendingSignup.completed == "1",
          disabledTooltip: pendingSignup.business_created == "0" ? "Business record(s) not created yet." : "Signup completed."
        },
        {
          // TODO: In the confirmation prompt, include inputs to edit the contact/user information before sending out?
          action: () => this.promptSendVerificationEmail(pendingSignup),
          label: "Send Verification Email",
          disabled: (pendingSignup.verification_email_sent == "1" && this.calculateDaysBetweenNow(pendingSignup.time_verification_email_sent) <= 3) || pendingSignup.completed == "1",
          disabledTooltip: "Email sent at " + moment.utc(pendingSignup.time_verification_email_sent).local().format("lll")
        },
        {
          action: () => this.promptDeletePendingSignup(pendingSignup),
          label: "Delete"
        }
      ];
    },
    promptSendVerificationEmail(pendingSignup) {
      Dialog.create({
        component: ConfirmationModal,
        componentProps: {
          header: "Send Verification Email",
          subHeader: pendingSignup.business_name,
          message:
            "The user will receive an email at <b>" + pendingSignup.contact_email + "</b> with a link to finalization creation & access their account. This email/link will expire in 96 hours. <b>Are you sure you want to continue?</b>"
        }
      })
        .onOk(dialog => {
          dialog.submitting = true;
          axios
            .post("http://localhost:8082/internal/insurance-agents/send-user-verification-email", {
              pending_signup_id: pendingSignup.id,
              business_id: pendingSignup.claimed_business_id,
              first_name: pendingSignup.contact_first_name,
              last_name: pendingSignup.contact_last_name,
              email: pendingSignup.contact_email,
              type: "admin"
            })
            .then(() => {
              this.triggerActionOutcomeAlert("send", true);
              dialog.hide();
              this.$router.go();
            })
            .catch(err => {
              console.log(err);
              this.triggerActionOutcomeAlert("send", false, err.response.data.errors[0].detail);
            })
            .finally(() => {
              dialog.submitting = false;
            });
        });
    },
    promptDeletePendingSignup(pendingSignup) {
      Dialog.create({
        component: ConfirmationModalDelete,
        componentProps: {
          descriptor: pendingSignup.business_name + ' (' + pendingSignup.id + ')'
        }
      }).onOk(dialog => {
        dialog.submitting = true;
        axios
          .post("http://localhost:8082/internal/insurance-agents/delete-pending-signup", {
            id: pendingSignup.id
          })
          .then(() => {
            this.triggerActionOutcomeAlert("delete", true);
            dialog.hide();
            this.$router.go();
          })
          .catch(err => {
            console.log(err);
            this.triggerActionOutcomeAlert("delete", false, err.response.data.errors[0].detail);
          })
          .finally(() => {
            dialog.submitting = false;
          });
      });
    }
  }
};
</script>

<style scoped lang="scss"></style>
